import React, {useContext, useRef, useState} from "react"
import {Stars} from "react-bootstrap-icons"
import {useTranslation} from "next-i18next"
import {useRouter} from "next/router"
import AnalyticsContext from "../../context/AnalyticsContext"
import {clarityEvents, gaCategories, gaEvents} from "../../config/googleAnalytics/events"
import {storeDataToSpreadsheet} from "../../utility/Helper"
import Typography from "../reusable/Typography/Typography"
import Button from "../reusable/components/Button"
import {colors} from "../../colors/Colors"
import Spinner from "../reusable/Spinner"

type Props = {
	comparisonProducts: string[]
}
function ComparisonAiAssistant({comparisonProducts}:Props) {
	const [input, setInput] = useState("")
	const [loading, setLoading] = useState(false)
	const [result, setResult] = useState("")
	const textArea = useRef(null)
	const router = useRouter()
	const {t} = useTranslation()
	const {reactGA} = useContext(AnalyticsContext)

	const handleSubmit = async e => {
		e.preventDefault()
		setLoading(true)

		if (typeof window !== "undefined") {
			window.clarity("set", clarityEvents.askedAi, "User Asked Comparison AI")
		}

		reactGA?.event({
			category: gaCategories.pdp,
			action: `${gaEvents.userAskedComparisonAi} ${input}`,
			label: `${gaEvents.userAskedComparisonAi} ${input}`,
			nonInteraction: false
		})

		const updatedMessages = input
		try {
			const res = await fetch(`/api/ai/chat/comparison/${comparisonProducts}?locale=${router.locale || "de"}`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({
					description: updatedMessages
				})
			})

			const data = await res.json()
			setResult(() => data.response)
			await storeDataToSpreadsheet({
				bike: comparisonProducts.join(", "),
				question: `${input}`,
				answer: data.response
			}, "comparisonAi")
		} catch (error) {
			console.error("Error communicating with the API:", error)
		}

		setLoading(false)
	}

	const handleAskAnotherQuestion = () => {
		setInput("")
		setResult("")
		textArea.current.focus()
	}

	return (
		<div
			className="d-flex align-items-center col-12 col-md-6 justify-content-start gap-3 py-3 flex-column">
			<div className="d-flex align-items-start justify-content-start gap-1 col-12">
				<Typography style={{fontSize: "14px", lineHeight: "20px"}}
					variant="bodySmBold">{t("ask-ai-about-bike-comparison")}</Typography>
				<div style={{padding: "4px 8px", borderRadius: "8px", background: "#FFF2DB"}}>
					<Typography style={{fontSize: "12px", lineHeight: "20px", color: "#785108"}}
						variant="bodySmBold">{t("badge-new")}</Typography>
				</div>
			</div>
			<form className="send-message  m-auto"
				onSubmit={handleSubmit}>
				<textarea
					ref={textArea}
					className="input-field"
					value={input}
					rows={2}
					onChange={e => setInput(e.target.value)}
					placeholder={t("pdp-ai-comparison-description")}
					required
				/>
				<Button type="submit"
					bgColor={colors.primary}
					style={{maxWidth: "130px"}}
					className="try-ai position-relative"
					fontSizeSmall
					label={t("ask-ai-label")}
					iconFirst
					icon={<><img src="/assets/icons/small-star.svg"
						alt="ai"
						width={12}
						height={12}
						style={{position: "absolute", left: "3px", bottom: "4px"}} />
					<img src="/assets/icons/small-star.svg"
						alt="ai"
						width={8}
						height={8}
						style={{position: "absolute", right: "3px", top: "4px"}} /></>}/>
			</form>
			{loading ? <Spinner /> : result?.length ? <div className={"ai-response-pdp slide-in-top"}>
				<div className="d-flex align-items-center justify-content-start gap-1 mt-2"><Stars />
					<Typography style={{fontSize: "14px", lineHeight: "20px"}}
						variant="bodySmBold">{t("ai-answer-label")}</Typography></div>
				<Typography variant="bodySm">{result}</Typography>
				<Typography onClick={handleAskAnotherQuestion}
					className="mt-2 cursor-pointer"
					style={{fontSize: "14px", lineHeight: "20px", color: "#956306"}}
					variant="bodySmBold">{t("ask-another-question-label")}</Typography>
			</div> : null}
		</div>
	)
}

export default ComparisonAiAssistant
